import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Copy,
  Trash2,
  Eye,
  MapPin,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/spaces/")({
  head: () => ({
    meta: [
      { title: "Spaces — Spayzo Merchant" },
      { name: "description", content: "Manage spaces, pricing, and availability." },
    ],
  }),
  component: SpacesPage,
});

type Status = "active" | "draft" | "offline";
type Space = {
  id: string;
  nameKey: string;
  image: string;
  price: number;
  status: Status;
  availability: number; // 0-100
  bookings: number;
  locationKey: string;
};

const seed: Space[] = [
  { id: "1", nameKey: "studioALoft", image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80", price: 42, status: "active", availability: 78, bookings: 24, locationKey: "downtown" },
  { id: "2", nameKey: "meetingLoft", image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&q=80", price: 64, status: "active", availability: 45, bookings: 18, locationKey: "midtown" },
  { id: "3", nameKey: "photoBay", image: "https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=800&q=80", price: 88, status: "active", availability: 92, bookings: 11, locationKey: "artsDistrict" },
  { id: "4", nameKey: "quietDesk3", image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=800&q=80", price: 12, status: "offline", availability: 0, bookings: 0, locationKey: "downtown" },
  { id: "5", nameKey: "podRoom", image: "https://images.unsplash.com/photo-1564540583246-934409427776?w=800&q=80", price: 22, status: "draft", availability: 60, bookings: 0, locationKey: "eastside" },
  { id: "6", nameKey: "rooftopLounge", image: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80", price: 120, status: "active", availability: 30, bookings: 7, locationKey: "downtown" },
];

const statusStyles: Record<Status, string> = {
  active: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20",
  draft: "bg-amber-500/10 text-amber-700 ring-amber-500/20",
  offline: "bg-muted text-muted-foreground ring-border",
};
const statusDot: Record<Status, string> = {
  active: "bg-emerald-500",
  draft: "bg-amber-500",
  offline: "bg-muted-foreground",
};

function SpacesPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Status>("all");
  const [spaces, setSpaces] = useState<Space[]>(seed);
  const [toDelete, setToDelete] = useState<Space | null>(null);

  const filtered = useMemo(
    () =>
      spaces.filter(
        (s) =>
          (statusFilter === "all" || s.status === statusFilter) &&
          t(`merchantSpaces.names.${s.nameKey}`).toLowerCase().includes(query.toLowerCase()),
      ),
    [spaces, query, statusFilter, t],
  );

  const duplicate = (s: Space) => {
    const copy: Space = { ...s, id: `${s.id}-copy-${Date.now()}`, status: "draft" };
    setSpaces((cur) => [copy, ...cur]);
    toast.success(t("merchantSpaces.toast.duplicatedTitle"), { description: t("merchantSpaces.toast.duplicatedDesc", { name: t(`merchantSpaces.names.${s.nameKey}`) }) });
  };

  const confirmDelete = () => {
    if (!toDelete) return;
    setSpaces((cur) => cur.filter((x) => x.id !== toDelete.id));
    toast.success(t("merchantSpaces.toast.deletedTitle"), { description: t("merchantSpaces.toast.deletedDesc", { name: t(`merchantSpaces.names.${toDelete.nameKey}`) }) });
    setToDelete(null);
  };

  return (
    <MerchantShell
      title={t("merchantSpaces.title")}
      subtitle={t("merchantSpaces.subtitle")}
      actions={
        <Button asChild className="h-10 rounded-xl shadow-soft">
          <Link to="/merchant/spaces/new"><Plus className="h-4 w-4" /> {t("merchantSpaces.addNewSpace")}</Link>
        </Button>
      }
    >
      {/* Toolbar */}
      <Card className="rounded-2xl border-border/60 p-3 shadow-soft sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("merchantSpaces.searchPlaceholder")}
              className="h-11 rounded-xl border-border/70 bg-surface ps-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="h-11 w-full rounded-xl sm:w-44">
              <SelectValue placeholder={t("merchantSpaces.allStatuses")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("merchantSpaces.allStatuses")}</SelectItem>
              <SelectItem value="active">{t("merchantSpaces.status.active")}</SelectItem>
              <SelectItem value="draft">{t("merchantSpaces.status.draft")}</SelectItem>
              <SelectItem value="offline">{t("merchantSpaces.status.offline")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{t("merchantSpaces.spacesCount", { count: filtered.length })}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t("merchantSpaces.activeCount", { count: spaces.filter(s=>s.status==="active").length })}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> {t("merchantSpaces.draftCount", { count: spaces.filter(s=>s.status==="draft").length })}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" /> {t("merchantSpaces.offlineCount", { count: spaces.filter(s=>s.status==="offline").length })}</span>
        </div>
      </Card>

      {/* Grid */}
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((s) => (
          <SpaceCard key={s.id} space={s} onDuplicate={() => duplicate(s)} onDelete={() => setToDelete(s)} />
        ))}
        {filtered.length === 0 && (
          <Card className="col-span-full rounded-2xl border-dashed p-12 text-center">
            <p className="text-sm font-semibold">{t("merchantSpaces.noMatch")}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t("merchantSpaces.tryClearing")}</p>
          </Card>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("merchantSpaces.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? t("merchantSpaces.deleteDesc", { name: t(`merchantSpaces.names.${toDelete.nameKey}`) }) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t("merchantSpaces.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("merchantSpaces.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MerchantShell>
  );
}


function SpaceCard({ space, onDuplicate, onDelete }: { space: Space; onDuplicate: () => void; onDelete: () => void }) {
  const { t } = useTranslation();
  const editHref = { to: "/merchant/spaces/$id/edit", params: { id: space.id } } as const;
  const spaceName = t(`merchantSpaces.names.${space.nameKey}`);
  return (
    <Card className="group overflow-hidden rounded-2xl border-border/60 p-0 shadow-soft transition hover:shadow-card">
      <div className="relative aspect-[16/10] overflow-hidden bg-muted">
        <img
          src={space.image}
          alt={spaceName}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
                decoding="async"
        />
        <span
          className={cn(
            "absolute start-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset backdrop-blur",
            statusStyles[space.status],
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", statusDot[space.status])} />
          {t(`merchantSpaces.status.${space.status}`)}
        </span>
        <div className="absolute end-3 top-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" aria-label={t("merchantSpaces.actions", "Actions")} variant="secondary" className="h-8 w-8 rounded-full bg-background/90 shadow-soft hover:bg-background">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem asChild><Link {...editHref}><Pencil className="h-4 w-4" /> {t("merchantSpaces.edit")}</Link></DropdownMenuItem>
              <DropdownMenuItem onSelect={onDuplicate}><Copy className="h-4 w-4" /> {t("merchantSpaces.duplicate")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" /> {t("merchantSpaces.delete")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-semibold">{spaceName}</p>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" /> {t(`merchantSpaces.locations.${space.locationKey}`)}
            </p>
          </div>
          <div className="text-end">
            <p className="font-display text-lg font-bold tracking-tight"><Riyal /> {space.price}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("merchantSpaces.perHour")}</p>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{t("merchantSpaces.availabilityThisWeek")}</span>
            <span className="font-semibold">{space.availability}%</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                space.availability > 60 ? "bg-emerald-500" : space.availability > 30 ? "bg-amber-500" : "bg-rose-500",
              )}
              style={{ width: `${space.availability}%` }}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button asChild size="sm" variant="outline" className="h-9 flex-1 rounded-lg">
            <Link {...editHref}><Pencil className="h-3.5 w-3.5" /> {t("merchantSpaces.edit")}</Link>
          </Button>
          <Button asChild size="sm" variant="ghost" className="h-9 rounded-lg">
            <Link to="/spaces/$spaceId" params={{ spaceId: space.id }}>
              <Eye className="h-3.5 w-3.5" /> {t("merchantSpaces.view")}
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}
